<script setup lang="ts">
import { ref, watch } from 'vue'
import type { PushDeliveryLog } from './types'
import { formatTime, providerLabel } from './usePushCenterState'
import {
  AdminDataTable,
  AdminFilterBar,
  AdminStatusTag,
  RrInput,
  RrTag,
  type TableColumnType
} from '@xjlv2/admin-layout'

const props = withDefaults(
  defineProps<{
    logs?: PushDeliveryLog[]
    taskId: string
    total: number
    page: number
    pageSize: number
    queryLoading?: boolean
  }>(),
  {
    logs: () => [],
    queryLoading: false
  }
)

const emit = defineEmits<{
  search: [taskId: string]
  'page-change': [pageNo: number, pageSize: number]
}>()

const taskIdInput = ref(props.taskId)
watch(
  () => props.taskId,
  (value) => {
    taskIdInput.value = value
  }
)

const columns: TableColumnType<PushDeliveryLog>[] = [
  { key: 'id', title: 'ID', width: 80 },
  { key: 'taskId', title: '任务 ID', width: 100 },
  { key: 'deviceId', title: '设备 ID', width: 100 },
  { key: 'provider', title: '通道', width: 120 },
  { key: 'success', title: '投递结果', width: 100 },
  { key: 'error', title: '错误信息', minWidth: 200 },
  { key: 'providerMessageId', title: '厂商消息 ID', width: 180 },
  { key: 'createdAt', title: '投递时间', width: 180 }
]

function handleSearch() {
  emit('search', taskIdInput.value)
}

function handleReset() {
  taskIdInput.value = ''
  emit('search', '')
}
</script>

<template>
  <div class="push-tab-pane">
    <AdminFilterBar @submit="handleSearch" @reset="handleReset">
      <label>
        任务 ID
        <RrInput
          v-model:value.trim="taskIdInput"
          placeholder="输入任务 ID 精确查询"
          @keyup.enter="handleSearch"
        />
      </label>
    </AdminFilterBar>

    <AdminDataTable
      :columns="columns"
      :data-source="logs"
      :current="page"
      :page-size="pageSize"
      :total="total"
      :loading="queryLoading"
      :empty="logs.length === 0"
      empty-title="暂无投递明细"
      empty-description="推送任务触发后将在此记录每条投递日志。"
      @update:current="(p) => emit('page-change', p, pageSize)"
      @update:page-size="(size) => emit('page-change', 1, size)"
      @refresh="handleSearch"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'id'">
          <code>#{{ record.id }}</code>
        </template>
        <template v-else-if="column.key === 'taskId'"> #{{ record.taskId }} </template>
        <template v-else-if="column.key === 'deviceId'"> #{{ record.deviceId }} </template>
        <template v-else-if="column.key === 'provider'">
          <RrTag tone="neutral" :label="providerLabel(record.provider)" />
        </template>
        <template v-else-if="column.key === 'success'">
          <AdminStatusTag
            :text="record.success ? '成功' : '失败'"
            :tone="record.success ? 'success' : 'danger'"
          />
        </template>
        <template v-else-if="column.key === 'error'">
          <span v-if="record.errorMessage" class="text-red"
            >{{ record.errorCode || '' }} {{ record.errorMessage }}</span
          >
          <span v-else class="text-muted">-</span>
        </template>
        <template v-else-if="column.key === 'providerMessageId'">
          <code>{{ record.providerMessageId || '-' }}</code>
        </template>
        <template v-else-if="column.key === 'createdAt'">
          <span class="text-time">{{ formatTime(record.createdAt) }}</span>
        </template>
      </template>
    </AdminDataTable>
  </div>
</template>

<style scoped>
.push-tab-pane {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-4);
}

.text-time {
  font-size: var(--rr-font-size-sm);
  color: var(--rr-color-text-muted);
}

.text-red {
  color: var(--rr-color-danger);
  font-weight: var(--rr-font-weight-bold);
}
.text-muted {
  color: var(--rr-color-text-muted);
}
</style>
